import React, { useState, useMemo } from 'react';
import { useScheduledEmails, useCancelScheduledEmail, ScheduledEmail } from '@/hooks/useEmailLogs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { X, Clock, CheckCircle, XCircle, User, RefreshCw, Eye, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';
import ScheduledEmailPreviewModal from './ScheduledEmailPreviewModal';

type SortField = 'scheduled_for' | 'to_email' | 'template_key' | 'status';
type SortDirection = 'asc' | 'desc';

const ScheduledEmailsTable = () => {
  const { data: emails, isLoading, refetch } = useScheduledEmails();
  const cancelEmail = useCancelScheduledEmail();
  const [previewEmail, setPreviewEmail] = useState<ScheduledEmail | null>(null);
  const [sortField, setSortField] = useState<SortField>('scheduled_for');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedEmails = useMemo(() => {
    if (!emails) return [];
    
    return [...emails].sort((a, b) => {
      let comparison = 0;
      
      switch (sortField) {
        case 'scheduled_for':
          comparison = new Date(a.scheduled_for).getTime() - new Date(b.scheduled_for).getTime();
          break;
        case 'to_email':
          comparison = a.to_email.localeCompare(b.to_email);
          break;
        case 'template_key':
          comparison = a.template_key.localeCompare(b.template_key);
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [emails, sortField, sortDirection]);

  const handleCancel = async (emailId: string) => {
    try {
      await cancelEmail.mutateAsync(emailId);
      toast.success('Email agendado cancelado');
    } catch (error) {
      toast.error('Erro ao cancelar email');
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'sent':
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/30"><CheckCircle className="w-3 h-3 mr-1" />Enviado</Badge>;
      case 'cancelled':
        return <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30"><X className="w-3 h-3 mr-1" />Cancelado</Badge>;
      case 'failed':
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30"><XCircle className="w-3 h-3 mr-1" />Falhou</Badge>;
      case 'processing':
        return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30"><RefreshCw className="w-3 h-3 mr-1 animate-spin" />Processando</Badge>;
      default:
        return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30"><Clock className="w-3 h-3 mr-1" />Pendente</Badge>;
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="w-3 h-3 ml-1 opacity-50" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1" />
      : <ArrowDown className="w-3 h-3 ml-1" />;
  };

  const SortableHeader = ({ field, children }: { field: SortField; children: React.ReactNode }) => (
    <th 
      className="text-left p-4 text-white/60 text-sm font-medium cursor-pointer hover:text-white/80 transition-colors select-none"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center">
        {children}
        <SortIcon field={field} />
      </div>
    </th>
  );

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-white/60 text-sm">
          Emails programados para envio futuro
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          className="bg-white/5 border-white/10 text-white hover:bg-white/10"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Atualizar
        </Button>
      </div>

      <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-white/60">Carregando...</div>
        ) : emails?.length === 0 ? (
          <div className="p-8 text-center text-white/60">
            <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Nenhum email agendado</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <SortableHeader field="scheduled_for">Agendado para</SortableHeader>
                  <SortableHeader field="to_email">Destinatário</SortableHeader>
                  <SortableHeader field="template_key">Template</SortableHeader>
                  <SortableHeader field="status">Status</SortableHeader>
                  <th className="text-left p-4 text-white/60 text-sm font-medium">Ações</th>
                </tr>
              </thead>
              <tbody>
                {sortedEmails.map((email) => (
                  <tr key={email.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="p-4">
                      <div className="text-white text-sm">
                        {format(new Date(email.scheduled_for), "dd/MM/yy HH:mm", { locale: ptBR })}
                      </div>
                      <div className="text-white/40 text-xs">
                        {formatDistanceToNow(new Date(email.scheduled_for), { addSuffix: true, locale: ptBR })}
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        {email.username && (
                          <span className="text-white/60 text-xs flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {email.username}
                          </span>
                        )}
                        <span className="text-white text-sm">{email.to_email}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <Badge variant="outline" className="bg-luxury-pinkBright/20 text-luxury-pinkBright border-luxury-pinkBright/30">
                        {email.template_key}
                      </Badge>
                    </td>
                    <td className="p-4">
                      {getStatusBadge(email.status)}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setPreviewEmail(email)}
                          className="text-luxury-pinkBright hover:text-luxury-pinkBright/80"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Preview
                        </Button>
                        {email.status === 'pending' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCancel(email.id)}
                            disabled={cancelEmail.isPending}
                            className="text-red-400 hover:text-red-300"
                          >
                            <X className="w-4 h-4 mr-1" />
                            Cancelar
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ScheduledEmailPreviewModal
        email={previewEmail}
        open={!!previewEmail}
        onOpenChange={(open) => !open && setPreviewEmail(null)}
        onResend={() => refetch()}
      />
    </div>
  );
};

export default ScheduledEmailsTable;
